// Claymation Ad's form (live's `claymation-ad-form/library-view`, spec G14-claymation-ad, PRs d1
// and d2; not captured, built from live's code): live's staged form in three stages. The script
// stage picks the product (one of the brand's, or an uploaded packshot) and the narrator (a voice),
// takes the five chips, and writes the script (the server's pack reader and director, edited by
// the member) to approve it. Approve script starts the narrator's takes and the storyboard: the
// voice stage picks a take and Approve voice plans the shots (`ClaymationStages.tsx`); once every
// image is drawn, "Generate final video" starts the run, which finishes in My Generations. The
// draft is the server's (`claymation-draft.ts`).
import { Button, Group, Loader, Stack, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Monitor, Music, Package, Smartphone } from "lucide-react";
import { useState, type ReactNode } from "react";

import { useGetCatalog, useListTemplateCaptionStyles } from "../../../api/generated/endpoints/generation/generation";
import type {
  ClaymationAdInputsProductCount,
  ClaymationAdInputsResolution,
  ClaymationNarrator,
} from "../../../api/generated/model";
import {
  claymationAdInputsBrandUrlMax,
  claymationAdInputsClosingLineMax,
  claymationAdInputsCtaMax,
  claymationAdInputsMechanismMax,
  claymationAdInputsPronunciationMax,
  VideoTemplateQuote,
  VideoTemplateRequest,
  type ClaymationAdInputsOutput,
} from "../../../api/generated/zod";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { usePricedStart } from "../../../platform/jobs/priced-start";
import { VoicePanel } from "../../../platform/voices/VoicePanel";
import { Choice } from "../../../ui/Choice";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { GlassPanel } from "../../../ui/GlassPanel";
import { scriptParts } from "./ClaymationScript";
import { StoryboardStage, VoiceStage } from "./ClaymationStages";
import { progressOf, useClaymationDraft, videoBody, type VideoSettings } from "./claymation-draft";
import {
  GENERATE_SCRIPT,
  PICK_NARRATOR,
  PICK_PRODUCT,
  UNREADABLE_PACK,
  WORD_BUDGET,
  beatLabel,
  countSaidWords,
  scriptIssue,
  scriptMeta,
} from "./claymation-script";
import { ReuseSwitch, type TemplateFormProps } from "./kit";
import { InputChip, ScriptSection } from "./PodcastScript";
import { Submit } from "./staged";
import { StreetProductTile, type ProductWords } from "./StreetTiles";
import { Subtitles, type SubtitlesValue } from "./Subtitles";
import { VoiceAudioTile } from "./voiceover";
import classes from "./podcast.module.css";

/** Live's words (`library-view/constants.ts`). */
const PRODUCT_WORDS: ProductWords = {
  tile:
    "The product and the exact photo of it the ad is built on. Pick one of your products and its " +
    "packshot — one pack, or two packs side by side — or upload one. The clay packs wear its real " +
    "label, and the end card shows it.",
  name: "The product name the script and the end card use. Leave empty and it is read off the packshot.",
  text:
    "A description or the product page. Every product fact in the script is quoted from it — leave " +
    "empty and the words printed on the pack are used.",
};
const NARRATOR_TOOLTIP =
  "The one off-camera voice that reads the whole script. Pick a warm, clear adult narrator and keep " +
  "it for every ad of this brand.";
const SCRIPT_TOOLTIP =
  "Eight narrated lines (nine with two products, 66–76 words) — the habit, the mechanism, the " +
  "reveal, the proof and the close — each with how the narrator says it, and the clay picture of " +
  "each of the eight shots. Edit anything before you approve; nothing is generated before that.";
const SUBTITLES_TOOLTIP =
  "Karaoke captions of the narration in short lines — the only text on the video, never on the end card.";
const PRODUCT_COUNT_TOOLTIP =
  "One product, or two products that do two different jobs, e.g. a day and a night cream. Two needs " +
  "one packshot with both packs side by side — pick or upload it in the Product tile. Auto decides " +
  "from the packshot and the product page.";
const MUSIC_BED_TOOLTIP = "A light acoustic music bed under the narrator, about 19 dB quieter than the voice.";
const RESOLUTION_TOOLTIP = "Resolution of every clip. 720p costs about 45% of 1080p.";
const FORMAT_TOOLTIP = "Every frame and clip is vertical 9:16.";
const SCRIPT_HINT = { wand: "to write the narrated lines and the eight clay shots from the product." };
const STAGE_ACTIONS = { script: "Approve script", voice: "Approve voice", storyboard: "Generate final video" };
const PICK_TAKE = "Pick a take";
const STILLS_PENDING = "Wait for every image";
const HANDOFF_NOTICE = "Your claymation ad is finishing in My Generations — you can close this panel now.";
const PRODUCT_COUNTS: readonly { value: ClaymationAdInputsProductCount; label: string }[] = [
  { value: "auto", label: "Auto" },
  { value: "1", label: "1 product" },
  { value: "2", label: "2 products" },
];
const RESOLUTIONS: readonly ClaymationAdInputsResolution[] = ["720p", "1080p"];
/** The run's style where the form names none (the recipe's `DEFAULT_CAPTION_STYLE`, live's
 * example's Karaoke). */
const DEFAULT_CAPTION_STYLE = "karaoke";
const CHIPS = [
  {
    field: "brand_url",
    label: "Brand URL",
    placeholder: "brandname.com (optional)",
    tooltip:
      "Your brand's web address, e.g. brandname.com, for the director — it is never drawn on the " +
      "video. Leave empty and it is read off the page or the pack.",
    maxLength: claymationAdInputsBrandUrlMax,
  },
  {
    field: "mechanism",
    label: "Mechanism",
    placeholder: "The one reason it works (optional)",
    tooltip:
      "The one reason the product works, in your brand's words — lines 3 and 4 explain it. Leave " +
      "empty and the director takes it from the product page.",
    maxLength: claymationAdInputsMechanismMax,
  },
  {
    field: "closing_line",
    label: "Closing line",
    placeholder: "No soft edges. (optional)",
    tooltip:
      '2 to 6 words that mirror the hook, e.g. "No soft edges." — the line under the product name ' +
      "on the end card. Leave empty and the director writes it.",
    maxLength: claymationAdInputsClosingLineMax,
  },
  {
    field: "cta",
    label: "CTA",
    placeholder: "Shop now (optional)",
    tooltip: "The button on the end card, word for word. Leave empty and the director writes a short one.",
    maxLength: claymationAdInputsCtaMax,
  },
  {
    field: "pronunciation",
    label: "Pronunciation",
    placeholder: "YOO-zoo (optional)",
    tooltip: "How your brand name is said, e.g. YOO-zoo. Leave empty and the director decides what needs a respelling.",
    maxLength: claymationAdInputsPronunciationMax,
  },
] as const;

/**
 * Live's form: "Reuse example" (off and disabled: the card has no example yet, G14-claymation-ad
 * open point 4), the stage, and in the footer the subtitles, the product count, the music bed, the
 * resolution, the 9:16 format and the round start, the stage's action, whose tooltip says what it
 * waits for. The product count locks with the script, as live's; the rest stays open until the
 * video starts.
 */
export function ClaymationAdForm(props: TemplateFormProps) {
  const { teamId, brandId, above, example, onStarted } = props;
  const [collapsed, setCollapsed] = useState(false);
  const flow = useClaymationDraft(teamId, brandId);
  const catalog = useGetCatalog().data;
  const styles = useListTemplateCaptionStyles(teamId).data;
  const [voicePanel, setVoicePanel] = useState(false);
  const [picked, setPicked] = useState<ClaymationNarrator | null>(null);
  const [chosen, setChosen] = useState<number | null>(null);
  const [productAsked, setProductAsked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [settings, setSettings] = useState<VideoSettings | null>(null);
  const { form, draft } = flow;
  const approved = draft !== undefined && draft.stage !== "script";
  if (!approved && settings !== null) setSettings(null);
  if (draft?.stage !== "voice" && chosen !== null) setChosen(null);
  const shownSettings: VideoSettings | null =
    form === null
      ? null
      : ((approved ? settings : null) ?? {
          subtitles: form.inputs.subtitles,
          caption_position: form.inputs.caption_position,
          caption_style_id: form.inputs.caption_style_id,
          music_bed: form.inputs.music_bed,
          resolution: form.inputs.resolution,
        });
  // Live's "Generate final video": the run finishes in My Generations; closing the panel stops nothing.
  const video = draft === undefined || shownSettings === null ? null : videoBody(draft, shownSettings);
  const videoRequest = VideoTemplateRequest.safeParse(video);
  const videoQuote = VideoTemplateQuote.safeParse(video);
  const videoStart = usePricedStart({
    teamId,
    brandId,
    quote: videoQuote.success ? videoQuote.data : undefined,
    request: videoRequest.success ? videoRequest.data : undefined,
    onStarted,
  });

  const panel = (body: ReactNode, footer?: ReactNode, aside?: ReactNode) => (
    <GlassPanel
      collapsed={collapsed}
      onCollapsedChange={setCollapsed}
      above={above}
      header={<ReuseSwitch example={null} checked={false} onChange={() => undefined} />}
      summary={<Text size="sm">Claymation Ad</Text>}
      footer={footer}
      aside={aside}
    >
      {body}
    </GlassPanel>
  );
  if (flow.read.isError) return panel(<ErrorAlert error={flow.read.error} />);
  if (draft === undefined || form === null || shownSettings === null) {
    return panel(<Loader size="sm" aria-label="Loading the draft" />);
  }

  const { inputs, script } = form;
  const setInputs = (change: Partial<ClaymationAdInputsOutput>) => flow.editInputs(change);
  const locked = !flow.editable;
  const changeSettings = (change: Partial<VideoSettings>) => {
    if (approved) setSettings({ ...shownSettings, ...change });
    else setInputs(change);
  };
  const subtitles: SubtitlesValue = {
    on: shownSettings.subtitles,
    position: shownSettings.caption_position,
    styleId: shownSettings.caption_style_id || DEFAULT_CAPTION_STYLE,
  };
  // The narrator by name: the one just picked, else the draft's as the server names it.
  const narrator = [picked, draft.narrator_voice].find((each) => each != null && each.id === inputs.narrator) ?? null;
  const progress = progressOf(flow.work);
  const writing = flow.work.reading || flow.work.writing;
  const issue = scriptIssue(script, inputs);
  // The take picked: the member's, else the one live pre-picks; Approve voice waits for the takes
  // alone, the stills drawing on (live's `busyByStage`).
  const take = chosen ?? draft.preferred_take;
  const takesBusy = flow.asking || flow.work.takes !== null;
  const starting = videoStart.start.isPending;

  let submit;
  if (draft.stage === "voice") {
    submit = (
      <Submit
        label={STAGE_ACTIONS.voice}
        reasons={take === null ? [PICK_TAKE] : []}
        disabled={take === null || takesBusy}
        loading={flow.approving}
        onClick={() => {
          if (take !== null) flow.approveVoice(take);
        }}
      />
    );
  } else if (draft.stage === "storyboard") {
    const ready = video !== null;
    submit = (
      <Submit
        label={STAGE_ACTIONS.storyboard}
        reasons={ready ? [] : [STILLS_PENDING]}
        disabled={!ready || flow.busy || !videoStart.canStart || starting}
        loading={starting}
        onClick={videoStart.startJob}
      />
    );
  } else {
    const reasons = [
      ...(inputs.product ? [] : [PICK_PRODUCT]),
      ...(inputs.narrator ? [] : [PICK_NARRATOR]),
      ...(script === null ? [GENERATE_SCRIPT] : []),
    ];
    submit = (
      <Submit
        label={STAGE_ACTIONS.script}
        reasons={reasons}
        disabled={reasons.length > 0 || issue !== null || flow.busy}
        loading={flow.approving}
        onClick={flow.approve}
      />
    );
  }
  const footer = (
    <Group justify="space-between" wrap="nowrap" gap={12}>
      <div className={classes.settings}>
        <Subtitles
          value={subtitles}
          styles={styles?.styles}
          tip={SUBTITLES_TOOLTIP}
          disabled={flow.busy}
          onChange={(next) =>
            changeSettings({ subtitles: next.on, caption_position: next.position, caption_style_id: next.styleId })
          }
        />
        <Choice
          variant="footer"
          setting="Products"
          icon={<Package size={16} strokeWidth={1.5} />}
          tip={PRODUCT_COUNT_TOOLTIP}
          width={120}
          value={inputs.product_count}
          options={[...PRODUCT_COUNTS]}
          disabled={locked}
          onChange={(next) => {
            const count = PRODUCT_COUNTS.find((each) => each.value === next);
            if (count !== undefined) setInputs({ product_count: count.value });
          }}
        />
        <Tooltip label={MUSIC_BED_TOOLTIP} multiline maw={280} withArrow>
          <UnstyledButton
            className={classes.setting}
            data-on={shownSettings.music_bed || undefined}
            aria-pressed={shownSettings.music_bed}
            disabled={flow.busy}
            onClick={() => changeSettings({ music_bed: !shownSettings.music_bed })}
          >
            <Music size={16} strokeWidth={1.5} aria-hidden />
            {shownSettings.music_bed ? "Music bed" : "No music"}
          </UnstyledButton>
        </Tooltip>
        <Choice
          variant="footer"
          setting="Resolution"
          icon={<Monitor size={16} strokeWidth={1.5} />}
          tip={RESOLUTION_TOOLTIP}
          width={80}
          value={shownSettings.resolution}
          options={RESOLUTIONS.map((value) => ({ value, label: value }))}
          disabled={flow.busy}
          onChange={(next) => {
            const resolution = RESOLUTIONS.find((each) => each === next);
            if (resolution !== undefined) changeSettings({ resolution });
          }}
        />
        <Tooltip label={FORMAT_TOOLTIP} multiline maw={280} withArrow>
          <span className={classes.setting}>
            <Smartphone size={16} strokeWidth={1.5} aria-hidden />
            9:16
          </span>
        </Tooltip>
      </div>
      {submit}
    </Group>
  );

  const warnings = [
    ...(draft.pack_warning ? [UNREADABLE_PACK] : []),
    ...(draft.script_warning === null ? [] : [draft.script_warning]),
  ];
  const refused = videoStart.start.error ?? videoStart.quoteError ?? flow.failure;
  const status = (
    <>
      {[draft.problem, ...warnings].map((line) =>
        line === null ? null : (
          <Text key={line} className={classes.problem} role="alert">
            {line}
          </Text>
        ),
      )}
      {refused === null ? null : <ErrorAlert error={refused} />}
      {progress === null ? null : (
        <output className={classes.progress}>
          <Loader size={14} aria-hidden />
          {progress}
        </output>
      )}
      {/* Live's toast, said in the panel (web/AGENTS.md: no toasts), and its hand-off line. */}
      {videoStart.start.isSuccess && draft.stage === "storyboard" ? (
        <Text component="output" size="sm">
          Generation started. {HANDOFF_NOTICE}
        </Text>
      ) : null}
      <ConfirmModal
        opened={confirming}
        title="Start over"
        confirmLabel="Start over"
        pending={flow.resetting}
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          flow.startOver();
          setConfirming(false);
        }}
      >
        <Text size="sm">The script, the takes, the shot spec and the images are cleared, and the form starts empty.</Text>
      </ConfirmModal>
    </>
  );

  if (approved && script !== null) {
    const stage =
      draft.stage === "voice" ? (
        <VoiceStage
          script={script}
          takes={draft.takes}
          note={draft.takes_note}
          chosen={take}
          busy={takesBusy}
          onPick={setChosen}
          onAddTake={flow.addTake}
        />
      ) : (
        <StoryboardStage
          script={script}
          spec={draft.spec}
          plan={draft.plan}
          stills={draft.stills}
          specWarning={draft.spec_warning}
          shotFailed={draft.shot_director === "failed"}
          drawing={flow.work.planning || flow.work.drawing !== null}
          busy={flow.busy}
          asking={flow.asking || starting}
          onRegenerate={flow.regenerate}
          onRetry={flow.retryShotDirector}
          onBack={flow.backToVoice}
        />
      );
    return panel(
      <div className={classes.content}>
        <Group justify="flex-end">
          <Button
            variant="subtle"
            size="compact-sm"
            disabled={flow.resetting || flow.busy}
            onClick={() => setConfirming(true)}
          >
            Start over
          </Button>
        </Group>
        {stage}
        {status}
      </div>,
      footer,
    );
  }

  const parts = script === null ? null : scriptParts(script, locked, flow.editScript);
  const body = (
    <div className={classes.content}>
      <Stack gap={12}>
        <div className={classes.tiles}>
          <StreetProductTile
            teamId={teamId}
            brandId={brandId}
            pick={inputs.product ?? null}
            typed={{ product_name: inputs.product_name, product_text: inputs.product_text }}
            disabled={locked}
            missing={productAsked}
            exampleUrls={example?.urls ?? {}}
            words={PRODUCT_WORDS}
            onChange={(product, typed) => setInputs({ product, ...typed, product_category: "" })}
          />
          <VoiceAudioTile
            voiceName={narrator?.name ?? null}
            tip={NARRATOR_TOOLTIP}
            disabled={locked}
            onOpen={() => setVoicePanel(true)}
          />
        </div>
        <div className={classes.chips}>
          {CHIPS.map((chip) => (
            <InputChip
              key={chip.field}
              label={chip.label}
              value={inputs[chip.field]}
              placeholder={chip.placeholder}
              tooltip={chip.tooltip}
              maxLength={chip.maxLength}
              disabled={locked}
              onChange={(value) => {
                const change: Partial<ClaymationAdInputsOutput> = {};
                change[chip.field] = value;
                setInputs(change);
              }}
            />
          ))}
        </div>
        <ScriptSection
          lines={
            script === null
              ? null
              : script.lines.map((line) => ({ id: line.id, speaker: null, beat: beatLabel(script, line.beat), text: line.text }))
          }
          meta={script === null ? null : scriptMeta(script)}
          tooltip={SCRIPT_TOOLTIP}
          primary={null}
          maxWords={() => WORD_BUDGET.line}
          countUnits={countSaidWords}
          hint={SCRIPT_HINT}
          writing={writing}
          locked={locked}
          issue={script === null || writing ? null : issue}
          warning={null}
          onGenerate={() => {
            if (!inputs.product) {
              setProductAsked(true);
              return;
            }
            flow.generateScript();
          }}
          onLine={(index, text) =>
            flow.editScript((current) => ({
              ...current,
              lines: current.lines.map((line, at) => (at === index ? { ...line, text } : line)),
            }))
          }
          {...(parts ?? {})}
        />
        {draft.script_problems.map((problem) => (
          <Text key={problem} className={classes.warning}>
            ⚠ {problem}
          </Text>
        ))}
      </Stack>
      {status}
    </div>
  );
  const aside = voicePanel ? (
    <VoicePanel
      teamId={teamId}
      brandId={brandId}
      chosen={narrator}
      onChoose={(voice) => {
        setPicked({ id: voice.id, name: voice.name, kind: voice.kind });
        setInputs({ narrator: voice.id });
      }}
      onClose={() => setVoicePanel(false)}
      designModel={catalog?.audio_models.find((each) => each.job_types.includes("voice.design"))}
    />
  ) : undefined;
  return panel(body, footer, aside);
}
